import { BehaviorSubject, race, timer } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import {
  getCurrentUserFromLocalStorage,
  removeCurrentUserFromLocalStorage,
  setCurrentUserInLocalStorage,
} from '../helpers/localStorageHelper';
import {
  getTokenExpiryTime,
  needsTokenRefresh
} from '../helpers/authHelper';

// Authentication timing constants
const PRE_REFRESH_TIME = 60000; // 1 minute (60 seconds * 1000ms) - refresh token 1 minute before it expires
const AUTH_READY_TIMEOUT = 500; // 0.5 seconds (500ms) - maximum time to wait for auth to be ready

class AuthenticationService {
  // Static method to get the singleton instance
  static instance() {
    if (!window._authServiceInstance) {
      window._authServiceInstance = new AuthenticationService();
    }
    return window._authServiceInstance;
  }

  refreshState$ = new BehaviorSubject();
  currentUser$ = new BehaviorSubject();
  loggingOutInProgress = false;
  
  constructor() {
    // Initialize refreshState$ with default values
    this.refreshState$.next({ 
      checked: false,
      refreshing: false 
    });
    const user = getCurrentUserFromLocalStorage();
    this.currentUser$.next(user);
  }

  async waitForAuthReady() {
    return new Promise((resolve, reject) => {
      // Create a stream that emits when auth is ready
      const authReady$ = this.refreshState$.pipe(
        filter(state => state.checked === true && state.refreshing === false),
        first(),
        map(() => 'AUTH_READY')
      );
      
      // Create a stream that emits after timeout
      const timeout$ = timer(AUTH_READY_TIMEOUT).pipe(
        map(() => 'TIMEOUT')
      );
      
      // Race between auth ready and timeout
      race(authReady$, timeout$)
        .subscribe(result => {
          if (result === 'AUTH_READY') {
            resolve();
          } else {
            resolve();
          }
        });
    });
  }

  markAuthReady(refreshing = false) {
    // Update the refreshState$ with the new state
    this.refreshState$.next({ 
      checked: true,
      refreshing: refreshing 
    });
  }

  async getToken() {
    try {
      const response = await fetch('/api/auth/token', {
        method: 'GET',
        // This ensures cookies are sent with the request
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get token');
      }

      return await response.json();
    } catch (error) {
      throw new Error('Failed to get token: ' + error.message);
    }
  }

  /**
   * Determines if the current JWT token needs to be refreshed
   * This function checks the token's expiration time and decides if refresh is needed
   * 
   * @returns {boolean} - true if token needs refresh, false otherwise
   * 
   * Token Refresh Logic:
   * 1. Check if user is logged in (has currentUser)
   * 2. Validate that access token exists
   * 3. Parse token to get expiration time
   * 4. Compare current time with expiration time
   * 5. Consider refresh needed if token expires within PRE_REFRESH_TIME (1 minute)
   * 
   * Safety Features:
   * - Returns false if no user is logged in (safe default)
   * - Assumes refresh is needed if token parsing fails (safe assumption)
   * - Logs warnings for debugging when issues occur
   */
  isTokenNeededToBeRefreshed(){
    // Step 1: Check if user is currently logged in
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      // No user logged in, no need to refresh token
      return false;
    }
    
    // Step 2: Get the JWT access token from current user
    const jwtAccessToken = currentUser.token;
    if (!jwtAccessToken) {
      // Token is missing, definitely need to refresh
      console.warn('No access token found in current user');
      return true; // Token is missing, need refresh
    }

    // Step 3: Use helper function to check if token needs refresh
    // This uses our safe needsTokenRefresh helper function
    const needsRefresh = needsTokenRefresh(jwtAccessToken, PRE_REFRESH_TIME);
    
    if (needsRefresh === null) {
      // If we can't determine, assume refresh is needed for safety
      console.warn('Could not determine token refresh status, assuming refresh needed');
      return true;
    }
    
    return needsRefresh;
  }

  async refreshToken() {
    try {
      const currentUser = this.getCurrentUser();
      const response = await fetch('/api/auth/token/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: currentUser.refreshToken,
          authenticationMethod: currentUser.authenticationMethod,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      return await response.json();
    } catch (error) {
      throw new Error('Failed to refresh token: ' + error.message);
    }
  }

  getCurrentUser() {
    return this.currentUser$.getValue();
  }

  getCurrentUser$() {
    return this.currentUser$.asObservable();
  }

  setCurrentUser(userObject) {
    try {
      setCurrentUserInLocalStorage(userObject);
      this.currentUser$.next(userObject);
    } catch (error) {
      throw new Error('Failed to set current user: ' + error.message);
    }
  }

  removeCurrentUser() {
    try {
      removeCurrentUserFromLocalStorage();
      this.currentUser$.next(null);
    } catch (error) {
      throw new Error('Failed to remove current user: ' + error.message);
    }
  }

  async forceLogout(redirectUri) {
    try {
      if (this.loggingOutInProgress) {
        // Prevent multiple logout attempts
        return;
      }
      // Set the flag to prevent multiple logouts
      this.loggingOutInProgress = true;
      this.removeCurrentUser();
      let path = window.location.pathname;
      if (redirectUri) {
        path = redirectUri;
      }
      window.open(`/auth/logout?returnUrl=${encodeURIComponent(path)}`, '_self');
      return true;
    } catch (error) {
      throw new Error('Failed to force logout: ' + error.message);
    }
  }
}

export { AuthenticationService };
export default AuthenticationService;
